import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  UIFrontendCoverageByType,
  UIFrontendProjectSummary,
  UIPRWithTypedCoverage,
} from '../../../../../../../api/teamMetricsApi';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  card: {
    padding: theme.spacing(3),
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  clickableCard: {
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    '&:hover': {
      borderColor: '#0070F3',
    },
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  label: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  infoIcon: {
    fontSize: 14,
    color: '#CBD5E0',
  },
  chevron: {
    fontSize: 14,
    color: '#CBD5E0',
    marginLeft: 'auto',
  },
  valueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
  },
  mainValue: {
    fontSize: 36,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  trendPositive: {
    fontSize: 14,
    fontWeight: 700,
    color: '#38A169',
  },
  trendNegative: {
    fontSize: 14,
    fontWeight: 700,
    color: '#E53E3E',
  },
  subtitle: {
    fontSize: 11,
    color: '#8E9299',
    marginTop: theme.spacing(0.5),
  },
  statRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  statItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  statLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: '#718096',
  },
  statDivider: {
    width: 1,
    height: 32,
    backgroundColor: '#E2E8F0',
  },
  viewPrsLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
    fontSize: 12,
    fontWeight: 600,
    color: '#0070F3',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  viewPrsIcon: {
    fontSize: 14,
  },
  coverageRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(4),
  },
  coverageItem: {
    flex: 1,
  },
  coverageLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: theme.spacing(0.5),
  },
  coverageValue: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1A1A1A',
  },
}));

interface FrontendHealthSummaryProps {
  aggregateCoverageByType: UIFrontendCoverageByType[];
  projectSummaries: UIFrontendProjectSummary[];
  pullRequests: UIPRWithTypedCoverage[];
  deploymentCount: number | null;
  onPRActivityClick?: () => void;
}

export const FrontendHealthSummary: React.FC<FrontendHealthSummaryProps> = ({
  projectSummaries,
  pullRequests,
  deploymentCount,
  onPRActivityClick,
}) => {
  const classes = useStyles();

  const projectsWithUnitCoverage = projectSummaries.filter((project) => {
    const coverage = project.coverageByType.find((c) => c.testType === 'unit');
    return coverage && coverage.metrics.statements.total > 0;
  });

  const projectsWithComponentCoverage = projectSummaries.filter((project) => {
    const coverage = project.coverageByType.find((c) => c.testType === 'component');
    return coverage && coverage.metrics.statements.total > 0;
  });

  const avgUnitPct = projectSummaries.length > 0
    ? projectSummaries.reduce((sum, project) => {
        const coverage = project.coverageByType.find((c) => c.testType === 'unit');
        return sum + (coverage?.metrics.statements.pct ?? 0);
      }, 0) / projectSummaries.length
    : 0;

  const avgComponentPct = projectSummaries.length > 0
    ? projectSummaries.reduce((sum, project) => {
        const coverage = project.coverageByType.find((c) => c.testType === 'component');
        return sum + (coverage?.metrics.statements.pct ?? 0);
      }, 0) / projectSummaries.length
    : 0;

  const totalStatements = projectSummaries.reduce((sum, project) => {
    const projectUnitCoverage = project.coverageByType.find((c) => c.testType === 'unit');
    return sum + (projectUnitCoverage?.metrics.statements.total ?? 0);
  }, 0);
  const prCount = pullRequests.length;

  return (
    <Box className={classes.container}>
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.labelRow}>
          <Typography className={classes.label}>Code Coverage</Typography>
          <Tooltip
            title="Average test coverage across frontend projects that have coverage data. The delta shows change since the start of the selected time period."
            arrow
            placement="top"
          >
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Box>
        <Box className={classes.coverageRow}>
          <Box className={classes.coverageItem}>
            <Typography className={classes.coverageLabel}>Unit (avg)</Typography>
            <Tooltip
              title={`Projects with coverage: ${projectsWithUnitCoverage.length}/${projectSummaries.length}`}
              arrow
              placement="top"
            >
              <Box display="flex" alignItems="baseline" style={{ cursor: 'help' }}>
                <Typography className={classes.coverageValue}>{avgUnitPct.toFixed(1)}%</Typography>
              </Box>
            </Tooltip>
          </Box>
          <Box className={classes.coverageItem}>
            <Typography className={classes.coverageLabel}>Component (avg)</Typography>
            <Tooltip
              title={`Projects with coverage: ${projectsWithComponentCoverage.length}/${projectSummaries.length}`}
              arrow
              placement="top"
            >
              <Box display="flex" alignItems="baseline" style={{ cursor: 'help' }}>
                <Typography className={classes.coverageValue}>{avgComponentPct.toFixed(1)}%</Typography>
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <Paper
        className={`${classes.card} ${onPRActivityClick ? classes.clickableCard : ''}`}
        elevation={0}
        onClick={onPRActivityClick}
      >
        <Box className={classes.labelRow}>
          <Typography className={classes.label}>Activity</Typography>
          {onPRActivityClick && <ChevronRightIcon className={classes.chevron} />}
        </Box>
        <Box className={classes.statRow}>
          <Box className={classes.statItem}>
            <Typography className={classes.statValue}>{prCount}</Typography>
            <Typography className={classes.statLabel}>PRs</Typography>
          </Box>
          <Box className={classes.statDivider} />
          <Box className={classes.statItem}>
            <Typography className={classes.statValue}>{deploymentCount ?? 0}</Typography>
            <Typography className={classes.statLabel}>Deploys</Typography>
          </Box>
        </Box>
        <Typography className={classes.subtitle}>During selected period</Typography>
        {onPRActivityClick && (
          <Box className={classes.viewPrsLink}>
            <Typography component="span" style={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
              View all team PRs
            </Typography>
            <ArrowForwardIcon className={classes.viewPrsIcon} />
          </Box>
        )}
      </Paper>

      <Paper className={classes.card} elevation={0}>
        <Box className={classes.labelRow}>
          <Typography className={classes.label}>Codebase Size</Typography>
          <Tooltip
            title="Total statements (from unit test coverage) across all frontend projects in the monorepo."
            arrow
            placement="top"
          >
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Box>
        <Box className={classes.valueRow}>
          <Typography className={classes.mainValue}>
            {totalStatements > 0 ? totalStatements.toLocaleString() : 'N/A'}
          </Typography>
        </Box>
        <Typography className={classes.subtitle}>Total statements across all projects</Typography>
      </Paper>
    </Box>
  );
};

export default FrontendHealthSummary;
